import React from "react";
import { Quill } from "react-quill";
import "./EditorToolbar.css";

// Import all custom SVG icons
import { ReactComponent as UndoIcon } from "../assets/MessageIcons/undo.svg";
import { ReactComponent as RedoIcon } from "../assets/MessageIcons/redo.svg";
import { ReactComponent as BoldIcon } from "../assets/MessageIcons/bold.svg";
import { ReactComponent as ItalicIcon } from "../assets/MessageIcons/italic.svg";
import { ReactComponent as UnderlineIcon } from "../assets/MessageIcons/underline.svg";
import { ReactComponent as TextColorIcon } from "../assets/MessageIcons/textcolor.svg";
import { ReactComponent as TextAlignIcon } from "../assets/MessageIcons/textalign.svg";
import { ReactComponent as OrderedListIcon } from "../assets/MessageIcons/orderedlist.svg";
import { ReactComponent as BulletListIcon } from "../assets/MessageIcons/bulletlist.svg";
import { ReactComponent as DecreaseIndentIcon } from "../assets/MessageIcons/decreaseindent.svg";
import { ReactComponent as IncreaseIndentIcon } from "../assets/MessageIcons/increaseindent.svg";
import { ReactComponent as BlockquoteIcon } from "../assets/MessageIcons/blackquote.svg";
import { ReactComponent as StrikeIcon } from "../assets/MessageIcons/strokethrough.svg";
import { ReactComponent as CancelTIcon } from "../assets/MessageIcons/canceltext.svg";
import { ReactComponent as SizeIcon } from "../assets/MessageIcons/size.svg";

// Register Custom Formats
const Size = Quill.import("formats/size");
Size.whitelist = ["extra-small", "small", "medium", "large"];
Quill.register(Size, true);

const Font = Quill.import("formats/font");
Font.whitelist = ["arial", "comic-sans", "courier-new", "georgia", "helvetica", "lucida"];
Quill.register(Font, true);

// Toolbar Component
export const QuillToolbar = ({ quillRef }) => {
  // Handlers
  const handleUndo = () => quillRef.current?.getEditor().history.undo();
  const handleRedo = () => quillRef.current?.getEditor().history.redo();
  const handleCancelT = () => {
    const range = quillRef.current?.getEditor().getSelection();
    if (range) {
      quillRef.current?.getEditor().formatText(range, 'strike', true);
      quillRef.current?.getEditor().formatText(range, 'color', '#ff0000');
    }
  };

  return (
    <div id="toolbar" className="custom-toolbar">
      {/* 1. Undo/Redo */}
      <span className="ql-formats">
        <button className="ql-undo" onClick={handleUndo} title="Undo">
          <UndoIcon className="toolbar-icon" />
        </button>
        <button className="ql-redo" onClick={handleRedo} title="Redo">
          <RedoIcon className="toolbar-icon" />
        </button>
      </span>

      <div className="toolbar-divider"></div>

      {/* 2. Font Family */}
      <span className="ql-formats">
        <div className="select-wrapper">
          <select className="ql-font" defaultValue="arial" title="Font">
            <option value="arial">Arial</option>
            <option value="comic-sans">Comic Sans</option>
            <option value="courier-new">Courier New</option>
            <option value="georgia">Georgia</option>
            <option value="helvetica">Helvetica</option>
            <option value="lucida">Lucida</option>
          </select>
        </div>
      </span>

      <div className="toolbar-divider"></div>

      {/* 3. Font Size */}
      <span className="ql-formats">
        <div className="select-wrapper">
          <select className="ql-size" defaultValue="medium" title="Size">
            <option value="extra-small">Size 1</option>
            <option value="small">Size 2</option>
            <option value="medium">Size 3</option>
            <option value="large">Size 4</option>
          </select>
        </div>
      </span>

      <div className="toolbar-divider"></div>

      {/* 4. Text Formatting */}
      <span className="ql-formats">
        <button className="ql-bold" title="Bold">
          <BoldIcon className="toolbar-icon" />
        </button>
        <button className="ql-italic" title="Italic">
          <ItalicIcon className="toolbar-icon" />
        </button>
        <button className="ql-underline" title="Underline">
          <UnderlineIcon className="toolbar-icon" />
        </button>
        <select className="ql-color" title="Text Color">
          <option value=""></option>
          <option value="#000000"></option>
          <option value="#e60000"></option>
          <option value="#ff9900"></option>
          <option value="#ffff00"></option>
          <option value="#008a00"></option>
          <option value="#0066cc"></option>
          <option value="#9933ff"></option>
          <option value="#ffffff"></option>
        </select>
      </span>


      <div className="toolbar-divider"></div>

      {/* 6. Text Alignment */}
      <span className="ql-formats">
        <select className="ql-align" title="Text Alignment">
          <option value=""></option>
          <option value="center"></option>
          <option value="right"></option>
          <option value="justify"></option>
        </select>

      </span>

      {/* 7. Lists & Indentation */}
      <span className="ql-formats">
        <button className="ql-list" value="ordered" title="Ordered List">
          <OrderedListIcon className="toolbar-icon" />
        </button>
        <button className="ql-list" value="bullet" title="Bullet List">
          <BulletListIcon className="toolbar-icon" />
        </button>
        <button className="ql-indent" value="-1" title="Decrease Indent">
          <DecreaseIndentIcon className="toolbar-icon" />
        </button>
        <button className="ql-indent" value="+1" title="Increase Indent">
          <IncreaseIndentIcon className="toolbar-icon" />
        </button>
      </span>

      <div className="toolbar-divider"></div>

      {/* 8. Blockquote */}
      <span className="ql-formats">
        <button className="ql-blockquote" title="Blockquote">
          <BlockquoteIcon className="toolbar-icon" />
        </button>
      </span>

      <div className="toolbar-divider"></div>

      {/* 9. Strikethrough and Canceled T */}
      <span className="ql-formats">
        <button className="ql-strike" title="Strikethrough">
          <StrikeIcon className="toolbar-icon" />
        </button>
        <button className="ql-cancelT" onClick={handleCancelT} title="Cancel Text">
          <CancelTIcon className="toolbar-icon" />
        </button>
      </span>
    </div>
  );
};

// Modules Configuration
export const modules = {
  toolbar: {
    container: "#toolbar",
    handlers: {
      undo: function() {
        this.quill.history.undo();
      },
      redo: function() {
        this.quill.history.redo();
      }
    }
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true
  }
};

// Formats Configuration
export const formats = [
  "header", "font", "size",
  "bold", "italic", "underline", "strike",
  "list", "bullet", "indent",
  "link", "image", "code-block",
  "color", "background", "align"
];



export default QuillToolbar;